import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { IConfirmDialogState } from 'store/reducers/ui/confirmDialog.reducer'
import { SET_THEME_STYLE } from 'store/reducers/ui/themeSetting.reducer'
import { CHANGE_GLOBAL_MENU } from 'store/reducers/ui/globalMenu.reducer'
import {
    CLOSE_ERROR_DIALOG,
    SHOW_ERROR_DIALOG,
} from 'store/reducers/ui/errorDialog.reducer'
import { useTranslation } from 'react-i18next'
import createDispatchAction, { AppThunk } from './base.action'
import { TOGGLE_HELP_DOCUMENT } from 'store/reducers/ui/helpDocument.reducer'

const DIALOG_DELETE_TEXT = 'dialog.message_delete'

export const SHOW_POS_HUB_DIALOG_WARNING = 'SHOW_POS_HUB_DIALOG_WARNING'
export const SHOW_POS_HUB_DIALOG_ERROR = 'SHOW_POS_HUB_DIALOG_ERROR'
export const CLOSE_POS_HUB_DIALOG_ERROR = 'CLOSE_POS_HUB_DIALOG_ERROR'

export interface ErrorMessage {
    arg: {}
    message: string | React.ReactNode
}

function _showConfirmDialog(props: Omit<IConfirmDialogState, 'open'>) {
    return {
        type: 'CONFIRM_DIALOG_SHOW',
        payload: props,
    }
}

const POS_HUB_MESSAGE_WARNING = 'POS Hub connection is lost, reconnecting...'

export const showPosHubDialogError = (errorMessage: string): AppThunk => (
    dispatch,
) => {
    dispatch(createDispatchAction(SHOW_POS_HUB_DIALOG_ERROR, errorMessage))
}

export const showPosHubDialogWarning = (): AppThunk => (dispatch) => {
    dispatch(
        createDispatchAction(
            SHOW_POS_HUB_DIALOG_WARNING,
            POS_HUB_MESSAGE_WARNING,
        ),
    )
}

export const hidePosHubDialog = (): AppThunk => (dispatch, state) => {
    dispatch(createDispatchAction(CLOSE_POS_HUB_DIALOG_ERROR))
}

export function useConfirmDialog() {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const _showErrorMessageDialogDispatch = useCallback(
        (message: ErrorMessage) => dispatch(showErrorMessage(message)),
        [dispatch],
    )
    const _showConfirmDialogDispatch = useCallback(
        (
            message: string | React.ReactNode,
            callback: (result: boolean) => void,
            title?: string,
            props?: Omit<IConfirmDialogState, 'title' | 'open' | 'message'>,
        ) =>
            dispatch(
                _showConfirmDialog({
                    ...(props ?? {}),
                    message,
                    callback,
                    title: title || 'Confirmation',
                }),
            ),
        [dispatch],
    )
    const closeDialog = useCallback(() => {
        dispatch({ type: 'CLOSE_DIALOG' })
    }, [dispatch])
    const _deleteConfirmDialog = useCallback(
        (
            callback: (result: boolean) => void,
            title = 'Confirm Delete',
            message?: string,
        ) =>
            _showConfirmDialogDispatch(
                message ? message : t(DIALOG_DELETE_TEXT),
                callback,
                title,
                { allowClose: true },
            ),
        [_showConfirmDialogDispatch, t],
    )
    const _showWarningDialog = useCallback(
        (
            message: string | React.ReactNode,
            callback: (result: boolean) => void,
            title?: string,
        ) =>
            dispatch(
                _showConfirmDialog({
                    message,
                    callback,
                    title: title || 'Warning',
                    isWarning: true,
                }),
            ),
        [dispatch],
    )

    const _showInfoDialog = useCallback(
        (
            title = 'Information',
            message: string | React.ReactNode,
            callback: () => void = () => {},
            option?: Pick<
                IConfirmDialogState,
                'confirmText' | 'hideConfirmBtn' | 'allowClose'
            >,
        ) => {
            dispatch(
                _showConfirmDialog({
                    ...(option ?? {}),
                    message,
                    callback,
                    title,
                    confirmText: option?.confirmText || 'Ok',
                    hideCancelBtn: true,
                    allowClose: true,
                }),
            )
        },
        [dispatch],
    )

    return {
        showConfirmDialog: _showConfirmDialogDispatch,
        deleteConfirmDialog: _deleteConfirmDialog,
        showErrorMessageDialog: _showErrorMessageDialogDispatch,
        showInfoDialog: _showInfoDialog,
        showWarningDialog: _showWarningDialog,
        closeDialog,
    }
}

export function setThemeMode(lightTheme: boolean) {
    return {
        type: SET_THEME_STYLE,
        payload: lightTheme,
    }
}

export function changeGlobalMenu(setIsOpen: boolean) {
    return {
        type: CHANGE_GLOBAL_MENU,
        payload: setIsOpen,
    }
}

export function showErrorMessage(error: any) {
    return {
        type: SHOW_ERROR_DIALOG,
        payload: error,
    }
}

export function closeErrorMessage() {
    return {
        type: CLOSE_ERROR_DIALOG,
    }
}

export function isUnauthorizedResult(err: any) {
    return err?.status === 401 || err?.response?.status === 401
}

export function toggleHelpDocument() {
    return {
        type: TOGGLE_HELP_DOCUMENT,
    }
}
